import React from 'react'
import { Button, Modal } from '@/components'
import { useLanguage } from '@/contexts/LanguageContext'
import type { useTurbineManagementViewModel } from '../useTurbineManagementViewModel'

type VM = ReturnType<typeof useTurbineManagementViewModel>

interface EditTurbineModalProps {
  showEditModal: VM['showEditModal']
  /** Tek kapanis yolu: backdrop / ESC / X / Iptal hepsi buraya duser. */
  onClose: VM['closeEditModal']
  editingTurbine: VM['editingTurbine']
  editTurbine: VM['editTurbine']
  setEditTurbine: VM['setEditTurbine']
  isSuperAdmin: VM['isSuperAdmin']
  companies: VM['companies']
  confirmEditTurbine: VM['confirmEditTurbine']
  isSubmitting: VM['isSavingTurbine']
  error: VM['editError']
  onDismissError: VM['dismissEditError']
}

const EditTurbineModal: React.FC<EditTurbineModalProps> = ({
  showEditModal,
  onClose,
  editingTurbine,
  editTurbine,
  setEditTurbine,
  isSuperAdmin,
  companies,
  confirmEditTurbine,
  isSubmitting,
  error,
  onDismissError,
}) => {
  const { t, language } = useLanguage()

  return (
    <Modal
      isOpen={showEditModal && !!editingTurbine}
      onClose={onClose}
      title={t.dashboard.editTurbine}
      size="md"
      // Guncelleme hatasi (or. ayni isimde turbin) buraya duser; eskiden
      // sayfa banner'ina yaziliyordu ve backdrop altinda kaldigi icin
      // kullaniciya HIC ulasmiyordu.
      error={error}
      onDismissError={onDismissError}
      isSubmitting={isSubmitting}
      footer={
        <div className="flex space-x-3">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
          >
            {t.dashboard.cancel}
          </Button>
          <Button
            onClick={confirmEditTurbine}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {t.dashboard.save}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Turbine Name -> neon 'turbine_name' */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.dashboard.turbineName}
          </label>
          <input
            type="text"
            value={editTurbine.turbine_name}
            onChange={(e) => setEditTurbine({...editTurbine, turbine_name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.turbineNamePlaceholder}
          />
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.dashboard.city} *
          </label>
          <input
            type="text"
            value={editTurbine.city}
            onChange={(e) => setEditTurbine({...editTurbine, city: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.cityName}
          />
        </div>

        {/* Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.dashboard.turbineCode} *
          </label>
          <input
            type="text"
            value={editTurbine.code}
            onChange={(e) => setEditTurbine({...editTurbine, code: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={t.dashboard.codePlaceholder}
          />
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t.dashboard.location} *
          </label>
          <textarea
            value={editTurbine.location}
            onChange={(e) => setEditTurbine({...editTurbine, location: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
            placeholder={t.dashboard.locationPlaceholder}
            rows={3}
          />
        </div>

        {/* Site Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {language === 'tr' ? 'Saha İsmi' : 'Site Name'}
          </label>
          <input
            type="text"
            value={editTurbine.site_name}
            onChange={(e) => setEditTurbine({...editTurbine, site_name: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={language === 'tr' ? 'Saha ismini girin' : 'Enter site name'}
          />
        </div>

        {/* Coordinates */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {language === 'tr' ? 'Koordinatlar' : 'Coordinates'}
          </label>
          <input
            type="text"
            value={editTurbine.coordinates}
            onChange={(e) => setEditTurbine({...editTurbine, coordinates: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder={language === 'tr' ? 'Örn: 41.0082,28.9784' : 'e.g: 41.0082,28.9784'}
          />
        </div>

        {/* Company Selection - Only for Superadmin */}
        {isSuperAdmin && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.dashboard.company} *
            </label>
            <select
              value={editTurbine.company_id}
              onChange={(e) => setEditTurbine({...editTurbine, company_id: parseInt(e.target.value)})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value={0}>{t.dashboard.selectCompany}</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default EditTurbineModal
